<template>
  <div
    ref="rootEl"
    class="mb-4"
  >
    <grid
      ref="participantsGrid"
      :show-header="true"
      :list-url="listUrl"
      loading-message="Loading participants ..."
      :add-checkmarks="false"
      :edit-action-as-link="false"
      :grid-table-class="gridTableClass"
      new-action-title="Add participant"
      :serialize-to-jsona="true"
      @item-action="onItemAction"
      @header-action="onHeaderAction"
      @meta-loaded="onGridMetaLoaded"
    >
      <template #custom-cell="{item, column}">
        <template v-if="column.name == 'extended_status'">
          <a
            v-if="showExtendedStatusMsg(item)"
            href="#"
            data-bs-toggle="popover"
            data-bs-html="true"
            :data-bs-title="item.extended_status.title"
            :data-bs-content="item.extended_status.message"
          >
            <span class="cursor-pointer glyphicons warning text-warning" />
          </a>
          <span v-else />
        </template>
      </template>
    </grid>
    <teleport
      to="#vue-portal-target"
      append
    >
      <candidate-modal
        ref="candidateModal"
        :modal-title="candidateModalProps.modalTitle"
        :search-url="candidateModalSearchUrl"
        :show-search="candidateModalProps.showSearch || false"
        :event="candidateModalProps.event"
        @success="onCandidateModalSuccess"
      />
      <candidate-details-modal
        ref="candidateDetailsModal"
      />
      <participant-invite-modal
        ref="inviteParticipantModal"
        @success="onInviteModalSuccess"
      />
      <xlsx-upload-modal
        ref="uploadParticipantsModal"
        :upload-url="xlsxUploadUrlProp"
        :template-url="xlsxUploadTemplateUrlProp"
      />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'

import Rails from '@rails/ujs'
import { Popover } from 'bootstrap'

import CandidateModal from './candidate-modal.vue'
import CandidateDetailsModal from './candidate-details-modal.vue'
import Grid from './grid.vue'
import ParticipantInviteModal from './participant-invite-modal.vue'
import XlsxUploadModal from './xlsx-upload-modal.vue'

import type { ButtonAction, GridHeaderAction } from './types'
import { isNonEmptyString } from '../js-utils'

interface ParticipantEvent {
  id: number;
}

interface CandidateModalProps {
  modalTitle?: string;
  searchUrl?: string | null;
  showSearch?: boolean;
  event?: ParticipantEvent | null;
}

defineProps({
  listUrl: { default: null, type: String },
  gridTableClass: { default: null, type: String },
})

const emit = defineEmits<{
  (e: 'contentschanged'): void;
}>()

const rootEl = useTemplateRef<HTMLElement>('rootEl')
const participantsGrid = useTemplateRef<InstanceType<typeof Grid>>('participantsGrid')
const candidateModal = useTemplateRef<InstanceType<typeof CandidateModal>>('candidateModal')
const candidateDetailsModal = useTemplateRef<InstanceType<typeof CandidateDetailsModal>>('candidateDetailsModal')
const inviteParticipantModal = useTemplateRef<InstanceType<typeof ParticipantInviteModal>>('inviteParticipantModal')
const uploadParticipantsModal = useTemplateRef<InstanceType<typeof XlsxUploadModal>>('uploadParticipantsModal')

const candidateModalProps = ref<CandidateModalProps>({})
const eventId = ref<number | null>(null)
const event = ref<ParticipantEvent | null>(null)
const xlsxUploadUrl = ref<string | null>(null)
const xlsxUploadTemplateUrl = ref<string | null>(null)

const candidateModalSearchUrl = computed<string | undefined>(() => {
  return candidateModalProps.value.searchUrl || undefined
})

const xlsxUploadUrlProp = computed((): string | undefined => {
  return xlsxUploadUrl.value || undefined
})

const xlsxUploadTemplateUrlProp = computed((): string | undefined => {
  return xlsxUploadTemplateUrl.value || undefined
})

let statusPopovers: Popover | null = null

onMounted(() => {
  if (rootEl.value) {
    Rails.delegate(rootEl.value, '[data-bs-toggle="popover"]', 'click', (e: Event) => {
      e.preventDefault()
    })
    statusPopovers = new Popover(rootEl.value, {
      selector: '[data-bs-toggle="popover"]',
      title: (el) => el.getAttribute('data-bs-title') ?? '',
      content: (el) => el.getAttribute('data-bs-content') ?? '',
    })
  }
})

onBeforeUnmount(() => {
  rootEl.value?.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => {
    Popover.getInstance(el)?.dispose()
  })
  statusPopovers?.dispose()
  statusPopovers = null
})

const reload = (): void => {
  participantsGrid.value?.reload()
}

const showExtendedStatusMsg = (item: any): boolean => {
  return item.extended_status != null
}

const onCandidateModalSuccess = (): void => {
  candidateModal.value?.hideModal()
  // reload candidates table view
  reload()
  emit('contentschanged')
}

const sendReimbursementInvitation = (invitationUrl: string): void => {
  if (confirm('This will send reimbursement invitation to single participant. Are you sure?')) {
    participantsGrid.value?.performRequest(invitationUrl, {}, { method: 'POST' }, (status: boolean, _response: unknown) => {
      if (status) {
        emit('contentschanged')
      }
    })
  }
}

const reSendReimbursementInvitation = (invitationUrl: string): void => {
  if (confirm('This will re-send reimbursement invitation to single participant. Are you sure?')) {
    participantsGrid.value?.performRequest(invitationUrl, {}, { method: 'POST' }, (status: boolean, _response: unknown) => {
      if (status) {
        emit('contentschanged')
      }
    })
  }
}

const sendAdditionalSurveyInvitation = (invitationUrl: string): void  => {
  if (confirm('This will send additional survey invitation to single participant. Are you sure?')) {
    participantsGrid.value?.performRequest(invitationUrl, {}, { method: 'POST' }, (status: boolean, _response: unknown) => {
      if (status) {
        emit('contentschanged')
      }
    })
  }
}

const reSendAdditionalSurveyInvitation = (invitationUrl: string): void => {
  if (confirm('This will re-send additional survey invitation to single participant. Are you sure?')) {
    participantsGrid.value?.performRequest(invitationUrl, {}, { method: 'POST' }, (status: boolean, _response: unknown) => {
      if (status) {
        emit('contentschanged')
      }
    })
  }
}

const showParticipantDetails = (detailsUrl: string): void => {
  candidateDetailsModal.value?.showModal(detailsUrl)
}

const inviteParticipant = (inviteUrl: string): void => {
  inviteParticipantModal.value?.showModal(inviteUrl, null)
}

const onInviteModalSuccess = (inviteUrl: string | null, messagingType: number): void => {
  inviteParticipantModal.value?.hideModal()
  if (!isNonEmptyString(inviteUrl)) {
    return
  }

  participantsGrid.value?.performRequest(inviteUrl, { message_type_id: messagingType }, { method: 'POST' }, (status: boolean, _response: unknown) => {
    if (status) {
      emit('contentschanged')
    }
  })
}

const unselectParticipant = (unselectUrl: string): void => {
  if (confirm('Are you sure?')) {
    participantsGrid.value?.performRequest(unselectUrl, {}, { method: 'POST' }, (_status: boolean, _response: unknown) => {
      emit('contentschanged')
    })
  }
}

const showNewParticipantModal = (newParticipantUrl: string, searchUrl: string | null | undefined): void => {
  candidateModalProps.value = {
    modalTitle: 'Add participant',
    searchUrl: searchUrl || null,
    showSearch: isNonEmptyString(searchUrl),
    event: event.value,
  }
  nextTick(() => {
    candidateModal.value?.showModal(newParticipantUrl, eventId.value)
  })
}

const showUploadParticipantsModal = (uploadUrl: string, templateUrl: string | null | undefined): void => {
  xlsxUploadUrl.value = uploadUrl
  xlsxUploadTemplateUrl.value = templateUrl || null

  uploadParticipantsModal.value?.showModal(event.value ? { event_id: event.value.id } : null)
}

const onItemAction = (item: any, action: any): void => {
  if (action === 'edit' || action.name == 'edit') {
    candidateModalProps.value = {
      modalTitle: 'Edit candidate',
    }
    const editUrl = `${item.links.edit}.json`
    candidateModal.value?.showModal(editUrl, null)
  } else if (action.action) {
    if (action.action === 'send-reimbursement-invitation') {
      sendReimbursementInvitation(action.url)
    } else if (action.action === 're-send-reimbursement-invitation') {
      reSendReimbursementInvitation(action.url)
    } else if (action.action === 'view-details') {
      showParticipantDetails(action.url)
    } else if (action.action === 'invite') {
      inviteParticipant(action.url)
    } else if (action.action === 'unselect') {
      unselectParticipant(action.url)
    } else if (action.action === 'send-additional-survey-invitation') {
      sendAdditionalSurveyInvitation(action.url)
    } else if (action.action === 're-send-additional-survey-invitation') {
      reSendAdditionalSurveyInvitation(action.url)
    } else {
      alert(`Application error. Unhandled action: '${action.action}'`)
    }
  } else {
    alert(`Application error. Unhandled action: '${JSON.stringify(action)}'`)
  }
}

const onHeaderAction = (
  actionOrName: string | GridHeaderAction | ButtonAction,
  url?: string,
  action?: GridHeaderAction | ButtonAction
): void => {
  const name = typeof actionOrName === 'string' ? actionOrName : actionOrName.name
  const actionUrl = typeof actionOrName === 'string' ? url : actionOrName.url
  const headerAction = typeof actionOrName === 'string' ? action : actionOrName
  const actionData = headerAction as any

  if (name === 'send-invitations') {
    if (isNonEmptyString(actionUrl)) {
      inviteParticipant(actionUrl)
    }
    return
  } else if (name === 'send-reimbursement' || name === 're-send-reimbursement') {
    if (confirm('This will send post-event reimbursement invitation to all participants. Are you sure?')) {
      if (isNonEmptyString(actionUrl)) {
        participantsGrid.value?.performRequest(actionUrl, {}, { method: 'POST' }, (_status: boolean, _response: unknown) => {
          emit('contentschanged')
        })
      }
    }
  } else if (name === 'send-additional-survey') {
    if (confirm('This will send additional survey invitation to all participants. Are you sure?')) {
      if (isNonEmptyString(actionUrl)) {
        participantsGrid.value?.performRequest(actionUrl, {}, { method: 'POST' }, (_status: boolean, _response: unknown) => {
          emit('contentschanged')
        })
      }
    }
  } else if (name === 're-send-additional-survey') {
    if (confirm('This will re-send additional survey invitation to all participants. Are you sure?')) {
      if (isNonEmptyString(actionUrl)) {
        participantsGrid.value?.performRequest(actionUrl, {}, { method: 'POST' }, (_status: boolean, _response: unknown) => {
          emit('contentschanged')
        })
      }
    }
  } else if (name === 'add-participant') {
    if (isNonEmptyString(actionUrl)) {
      showNewParticipantModal(actionUrl, actionData?.search_candidates_url)
    }
  } else if (name === 'upload-participant') {
    if (isNonEmptyString(actionData?.url)) {
      showUploadParticipantsModal(actionData.url, actionData.upload_template_url)
    }
  } else {
    alert(`Application error. Unhandled header action: '${name}'`)
  }
}

const onGridMetaLoaded = (meta: any): void => {
  event.value = null
  eventId.value = null
  if (meta.event != null) {
    event.value = meta.event
  }
  if (meta.eventId != null) {
    eventId.value = meta.eventId
  }
}

defineExpose({
  reload,
})
</script>
